import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { decideRefund, listRefundReviews, reconcileRefunds } from "@/lib/api";
import { DECISION_LABEL, confirmText, errorText, isFinal, statusLabel, validateDecision, NOTE_MAX } from "@/lib/refunds";

const input = "rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white";
const card = "rounded border border-slate-800 bg-slate-900 p-4";
const FILTERS = [["", "Semua"], ["pending_review", "Menunggu"], ["clawback_pending", "Berjalan"], ["completed_clawback", "Selesai"], ["closed_no_clawback", "Ditutup"], ["rejected", "Ditolak"]];

function ReviewCard({ review, onDecide, busy }) {
  const [decision, setDecision] = useState("");
  const [note, setNote] = useState("");
  const [credits, setCredits] = useState("");
  const [problem, setProblem] = useState("");
  const order = review.order || {};
  const final = isFinal(review.status);
  const submit = () => {
    const message = validateDecision({ decision, note, credits, granted: order.credits_granted, credited: order.credited });
    setProblem(message);
    if (message) return;
    if (!window.confirm(confirmText({ decision, credits, granted: order.credits_granted }))) return;
    onDecide(review.order_id, { decision, note: note.trim(), ...(decision === "clawback" && credits !== "" ? { credits: Number(credits) } : {}) }, setProblem);
  };
  return (
    <article className={card} data-testid={`refund-${review.order_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-white" data-testid={`refund-status-${review.order_id}`}>{statusLabel(review.status)}</p>
        <p className="text-xs text-slate-400">Pesanan {order.merchant_ref || review.order_id}</p>
      </div>
      <dl className="mt-3 grid gap-2 text-sm text-slate-300 sm:grid-cols-4">
        <div><dt className="text-xs text-slate-500">Nominal</dt><dd>{order.amount_idr == null ? "Belum tersedia" : `Rp${Number(order.amount_idr).toLocaleString("id-ID")}`}</dd></div>
        <div><dt className="text-xs text-slate-500">Kredit diberikan</dt><dd>{order.credited ? order.credits_granted : "Tidak pernah dikreditkan"}</dd></div>
        <div><dt className="text-xs text-slate-500">Status pesanan</dt><dd>{order.status}</dd></div>
        <div><dt className="text-xs text-slate-500">Pengguna</dt><dd className="break-all">{order.user_id}</dd></div>
      </dl>
      {review.last_error === "insufficient_balance" && !final && <p role="alert" className="mt-3 rounded bg-amber-950 p-2 text-sm text-amber-200" data-testid={`refund-insufficient-${review.order_id}`}>Penarikan sebelumnya ditolak: saldo pengguna kurang (kredit mungkin sudah terpakai). Pilih jumlah lebih kecil atau tutup tanpa penarikan.</p>}
      {final ? (
        <p className="mt-3 text-sm text-slate-300" data-testid={`refund-final-${review.order_id}`}>Keputusan final{review.credits ? `: ${review.credits} kredit` : ""}. Catatan: {review.note}</p>
      ) : review.status === "clawback_pending" ? (
        <p className="mt-3 text-sm text-slate-300">Penarikan {review.credits} kredit sedang diselesaikan. Jalankan “Cek konsistensi” bila status tidak berubah.</p>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Keputusan">
            {Object.entries(DECISION_LABEL).map(([key, label]) => (
              <label key={key} className="flex items-center gap-1.5 text-sm text-slate-200"><input type="radio" name={`d-${review.order_id}`} checked={decision === key} onChange={() => setDecision(key)} data-testid={`refund-decision-${key}-${review.order_id}`} />{label}</label>
            ))}
          </div>
          {decision === "clawback" && <label className="block text-sm text-slate-300">Jumlah kredit (kosong = semua {order.credits_granted})<input className={`${input} ml-2 w-28`} inputMode="numeric" value={credits} onChange={(e) => setCredits(e.target.value)} data-testid={`refund-credits-${review.order_id}`} /></label>}
          <label className="block text-sm text-slate-300">Catatan keputusan (wajib)
            <textarea className={`${input} mt-1 w-full`} rows={2} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`refund-note-${review.order_id}`} />
          </label>
          {problem && <p role="alert" className="text-sm text-red-300" data-testid={`refund-problem-${review.order_id}`}>{problem}</p>}
          <button className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" disabled={busy} onClick={submit} data-testid={`refund-submit-${review.order_id}`}>{busy ? "Memproses…" : "Simpan keputusan"}</button>
        </div>
      )}
    </article>
  );
}

export default function AdminRefunds() {
  const [state, setState] = useState({ status: "loading", reviews: [] });
  const [filter, setFilter] = useState("pending_review");
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState("");
  const lock = useRef(false);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    try {
      const data = await listRefundReviews(filter, 0);
      if (mine === ticket.current) setState({ status: "ready", reviews: data.reviews || [], total: data.total ?? (data.reviews || []).length });
    } catch (error) { if (mine === ticket.current) setState({ status: "error", reviews: [], message: errorText(error) }); }
  }, [filter]);
  useEffect(() => { setState((s) => ({ ...s, status: "loading" })); load(); }, [load]);

  const onDecide = async (orderId, payload, setProblem) => {
    if (lock.current) return;                                          // one decision request at a time: no double submit
    lock.current = true; setBusyId(orderId); setNotice("");
    try {
      await decideRefund(orderId, payload);
      setNotice("Keputusan tersimpan.");
      await load();
    } catch (error) { setProblem(errorText(error)); await load(); }
    finally { lock.current = false; setBusyId(null); }
  };
  const loadMore = async () => {
    if (lock.current) return;
    lock.current = true; setBusyId("more");
    try {
      const data = await listRefundReviews(filter, state.reviews.length);
      setState((s) => ({ ...s, reviews: [...s.reviews, ...(data.reviews || []).filter((r) => !s.reviews.some((x) => x.order_id === r.order_id))], total: data.total ?? s.total }));
    } catch (error) { setNotice(errorText(error)); }
    finally { lock.current = false; setBusyId(null); }
  };
  const reconcile = async () => {
    if (lock.current) return;
    lock.current = true; setBusyId("reconcile"); setNotice("");
    try {
      const r = await reconcileRefunds();
      setNotice(r.ok ? `Konsisten. Dilanjutkan: ${r.redriven.length}. Belum ditinjau: ${r.unreviewed_flagged}.` : `Ada ketidakcocokan pada ${(r.completed_without_receipt || []).length + (r.over_granted || []).length} tinjauan. Periksa ledger.`);
      await load();
    } catch (error) { setNotice(errorText(error)); }
    finally { lock.current = false; setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8" data-testid="admin-refunds">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-heading text-xl font-semibold">Tinjauan Refund</h1>
          <Link to="/admin" className="text-sm text-indigo-300 hover:underline">Kembali ke admin</Link>
        </div>
        <p className="rounded border border-slate-800 bg-slate-900 p-3 text-sm text-slate-300" data-testid="refund-scope-note">Halaman ini hanya memutuskan nasib <strong>kredit</strong> dari pesanan yang dilaporkan refund oleh gateway. Sistem <strong>tidak mengirim uang</strong> ke pelanggan dan tidak mengubah status pembayaran. Penarikan tidak pernah membuat saldo negatif.</p>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([key, label]) => <button key={key || "all"} className={`rounded px-3 py-1.5 text-sm ${filter === key ? "bg-indigo-600" : "bg-slate-800 text-slate-300"}`} aria-pressed={filter === key} onClick={() => setFilter(key)} data-testid={`refund-filter-${key || "all"}`}>{label}</button>)}
          <button className="ml-auto rounded bg-slate-800 px-3 py-1.5 text-sm text-slate-200 disabled:opacity-50" disabled={busyId === "reconcile"} onClick={reconcile} data-testid="refund-reconcile">Cek konsistensi</button>
        </div>
        {notice && <p role="status" className="text-sm text-emerald-300" data-testid="refund-notice">{notice}</p>}
        {state.status === "loading" && <p className="text-sm text-slate-400" data-testid="refund-loading">Memuat…</p>}
        {state.status === "error" && <p role="alert" className="text-sm text-red-300" data-testid="refund-error">{state.message} <button className="underline" onClick={load}>Coba lagi</button></p>}
        {state.status === "ready" && state.reviews.length === 0 && <p className="text-sm text-slate-400" data-testid="refund-empty">Tidak ada tinjauan refund pada filter ini.</p>}
        {state.status === "ready" && state.reviews.map((review) => <ReviewCard key={review.order_id} review={review} busy={busyId === review.order_id} onDecide={onDecide} />)}
        {state.status === "ready" && state.reviews.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400" data-testid="refund-paging">
            <span data-testid="refund-count">Menampilkan {state.reviews.length} dari {state.total}</span>
            {state.reviews.length < state.total && <button className="rounded bg-slate-800 px-3 py-1.5 text-slate-200 disabled:opacity-50" disabled={busyId === "more"} onClick={loadMore} data-testid="refund-more">Muat lebih banyak</button>}
          </div>
        )}
      </div>
    </div>
  );
}
